import { HTTP_STATUS_OK } from "../../smartpak/httpstatus";
import { antiForgeryPost } from "../../utils/fetch";
import { CHECKBOX, RADIOBUTTON } from "./constants/questiontypes";

const supportedDisplayTypeIds = [
    RADIOBUTTON,
    CHECKBOX
];

export default function questionsManager({ id, questions }) {
    const questionsList = deepClone(questions.filter(question => question.IsActive));

    if (questionsList.some(question => !supportedDisplayTypeIds.includes(question.DisplayTypeId))) {
        throw new Error('questionsManager does not support one of the question display types');
    }

    const questionsMap = new Map(questionsList.map(question => [question.QuestionId, question]));

    const {
        questionsWithPrerequisites,
        questionsWithoutPrerequisites
    } = groupByPrequisite(questionsList);

    const get = (questionId) => questionsMap.get(questionId);

    const set = (questionId, answers) => {
        const question = get(questionId);
        const previousAnswers = question.SelectedAnswers;

        question.SelectedAnswers = Array.isArray(answers) && answers.length ? answers : null;

        if (previousAnswers && previousAnswers.length) {
            clearPrerequisitesByRemoved(previousAnswers.filter(answer => !answers.includes(answer)));
        }
    };

    const clearPrerequisitesByRemoved = removedAnswers => {
        const currentAnswers = questionsList
            .filter(hasSelectedAnswers)
            .reduce((arr, question) => arr.concat(question.SelectedAnswers), []);

        questionsWithPrerequisites
            .filter(question => 
                question.PrerequisiteAnswerIds.some(id => removedAnswers.includes(id))
                && !question.PrerequisiteAnswerIds.some(id => currentAnswers.includes(id))
            )
            .forEach(question => question.SelectedAnswers = null);
    };

    const list = () => {
        const questionIdsUsed = [];

        const questionAnswersMap = new Map(
            questionsList
                .filter(hasSelectedAnswers)
                .map(question => [question.QuestionId, question.Answers.filter(answer => question.SelectedAnswers.includes(answer.AnswerId))])
        );

        let questionsToList = questionsWithoutPrerequisites.reduce((arr, question) => {
            arr.push(question);
            questionIdsUsed.push(question.QuestionId);

            if (questionAnswersMap.has(question.QuestionId)) {
                const answers = questionAnswersMap.get(question.QuestionId);

                answers
                    .filter(answer => answer.NextQuestionId && questionsMap.has(answer.NextQuestionId))
                    .sort((a, b) => a.NextQuestionId - b.NextQuestionId)
                    .forEach(answer => {
                        if (questionIdsUsed.includes(answer.NextQuestionId)){
                            return;
                        }
                        arr.push(questionsMap.get(answer.NextQuestionId));
                        questionIdsUsed.push(answer.NextQuestionId);
                    });
            }

            return arr;
        }, []);
        
        [...questionAnswersMap.values()].forEach((answers) => {
            const answerIds = answers.map(answer => answer.AnswerId);

            questionsWithPrerequisites
                .filter(question => question.PrerequisiteAnswerIds.some(id => answerIds.includes(id)))
                .forEach(question => {
                    if (questionIdsUsed.includes(question.QuestionId)) {
                        return;
                    }

                    questionsToList.push(question);
                    questionIdsUsed.push(question.QuestionId);
                });
        });

        return questionsToList;
    };

    const save = async (accountName) => {
        const errorMessage = 'An error occurred.';

        const response = await antiForgeryPost('/api/productpicker/SaveResponses', {
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ProductFinderHistoryId: id,
                AccountName: accountName,
                Answers: list().reduce((arr, question) => {
                    if (question.SelectedAnswers) {
                        arr = arr.concat(question.SelectedAnswers);
                    }
                    return arr;
                }, [])
            })
        });

        if (!response.ok) {
            throw new Error(errorMessage);
        }

        const { status, errors, content } = await response.json();

        if (status !== HTTP_STATUS_OK || (errors && errors.length > 0)) {
            throw new Error(errorMessage);
        }

        return content;
    };

    return {
        get,
        list,
        save,
        set
    };
}

function hasSelectedAnswers(question){
    return Array.isArray(question.SelectedAnswers) && question.SelectedAnswers.length > 0;
}

function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
}

function groupByPrequisite(questions){
    const hasPrerequisiteAnswerIds = question => Array.isArray(question.PrerequisiteAnswerIds) && question.PrerequisiteAnswerIds.length > 0;

    const {
        questionsWithPrerequisites,
        questionsWithoutPrerequisites
    } = questions.reduce((obj, question) => {
        if (hasPrerequisiteAnswerIds(question)) {
            obj.questionsWithPrerequisites.push(question);
        } else {
            obj.questionsWithoutPrerequisites.push(question);
        }

        return obj;
    }, {
        questionsWithPrerequisites: [],
        questionsWithoutPrerequisites: []
    });


    return {
        questionsWithPrerequisites,
        questionsWithoutPrerequisites
    };
}
